import React, { useCallback, useState } from 'react'
import { useAppDispatch } from '../../../hooks'
import { updateQrTag } from '../../../modules/QrTag'
import { closeModal, showModal } from '../../../modules/UI'
import { ERROR_MODAL } from '../../common/RootModal'
import { getErrorString } from '../../../utils/errorUtils'
import './EditQrTagModal.css'

type ModalProps = {
  qrTag?: IQrTag
  group?: IGroup
  onUpdated?: (qrTag: IQrTag) => void
  title?: string
  children?: React.ReactElement
}

type Props = {
  closeModalFunction?: () => void
  modalProps?: ModalProps
}

const EditQrTagModal = ({
  closeModalFunction,
  modalProps = {},
}: Props) => {
  const dispatch = useAppDispatch()
  const initialName = modalProps.qrTag?.name || ''
  const initialDescription = modalProps.qrTag?.description || ''
  const [name, setName] = useState(initialName)
  const [description, setDescription] = useState(initialDescription)
  const [isSaving, setIsSaving] = useState(false)

  const showErrorModal = useCallback((message: string) => {
    dispatch(closeModal({ modalType: ERROR_MODAL }))
    dispatch(showModal({
      modalType: ERROR_MODAL,
      modalProps: {
        message,
      },
    }))
  }, [dispatch])

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) {
      showErrorModal('QRタグの名前を入力してください。')
      return
    }

    if (!modalProps.qrTag || !modalProps.group) {
      showErrorModal('QRタグ情報の取得に失敗しました。再度お試しください。')
      return
    }

    try {
      setIsSaving(true)
      const updatedTag = await updateQrTag(
        modalProps.group.id,
        modalProps.qrTag.id,
        {
          name: name.trim(),
          description: description.trim() || null,
        },
      )
      modalProps.onUpdated?.(updatedTag)
      if (closeModalFunction) closeModalFunction()
    } catch (error) {
      showErrorModal(getErrorString(error))
    } finally {
      setIsSaving(false)
    }
  }, [closeModalFunction, description, modalProps, name, showErrorModal])

  if (!modalProps.qrTag || !modalProps.group) {
    return (
      <div className="edit-qr-tag-modal modal-form">
        <p>QRタグ情報を読み込めませんでした。ページを再読み込みして再度お試しください。</p>
        <div className="edit-qr-tag-modal__actions">
          <button
            type="button"
            className="btn btn-outline-primary cancel-button"
            onClick={closeModalFunction}
          >
            閉じる
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="edit-qr-tag-modal modal-form">
      <div className="edit-qr-tag-form">
        <label htmlFor="qr-tag-name">名前*</label>
        <input
          id="qr-tag-name"
          type="text"
          value={name}
          placeholder="例: 備品QRタグ"
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="edit-qr-tag-form">
        <label htmlFor="qr-tag-description">説明</label>
        <textarea
          id="qr-tag-description"
          value={description}
          placeholder="利用目的や設置場所などをメモできます"
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
      </div>
      <div className="edit-qr-tag-modal__actions">
        <button
          type="button"
          className="btn btn-primary save-button"
          disabled={isSaving}
          onClick={handleSubmit}
        >
          {isSaving ? '保存中...' : '保存'}
        </button>
        <button
          type="button"
          className="btn btn-outline-primary cancel-button"
          onClick={closeModalFunction}
          disabled={isSaving}
        >
          キャンセル
        </button>
      </div>
    </div>
  )
}

export default EditQrTagModal
